import React, {Suspense, useEffect} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApiNa} from '@di-shared/api/di';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useQueries, useQueryClient, useSuspenseQueries, useSuspenseQuery} from '@tanstack/react-query';
import {convertToRecommendedTireData, getPressureUnitString, isSingleSpec} from './tirePressureUtils.na';
import type {PressureItem, SelectedTireNA, TireData, TirePositionType, TirePressureUnit} from './types';

// ============================================================================
// InstructionSection
// ============================================================================

function InstructionSection() {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container">
      <div className="section_container_header">
        <h3 className="title_xsmall_b">{t('ctn_tire_main_measuring_method_title_eu')}</h3>
      </div>
      <p className="tire_pressure_desc">{t('msg_tire_main_measuring_method_eu')}</p>
      <div className="divider" />
      <div className="section_container_header">
        <h3 className="title_xsmall_b">{t('ctn_tire_main_info_title_eu')}</h3>
      </div>
      <p className="tire_pressure_desc">{t('msg_tire_main_info_eu')}</p>
    </div>
  );
}

// ============================================================================
// NoticeSection
// ============================================================================

function NoticeSection({vin}: {vin: string}) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense
        fallback={
          <section className="section section_notice skeleton">
            <ul className="notice_list">
              <li className="notice_item">&nbsp;</li>
            </ul>
          </section>
        }
      >
        <NoticeSectionInner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function NoticeSectionInner({vin}: {vin: string}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {
    data: {data: tireBoundData},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.tire.getTireBound), vin],
    queryFn: () => diApiNa.tire.getTireBound({vin}),
  });

  const updateDate = parseDateWithFormat(tireBoundData.tireTime);
  const updateDateString = updateDate ? CldrDatetimeFormatter.format(updateDate as Date, 'yMMMd') : '';

  return (
    <section className="section section_notice">
      <ul className="notice_list">
        <li className="notice_item">
          {t('msg_tire_temperature_range_eu', {date: updateDateString, temp: Math.round(tireBoundData.cstaAvg)})}
        </li>
      </ul>
    </section>
  );
}

// ============================================================================
// TireVisualSection
// ============================================================================

function TireInfoDisplay({
  position,
  pressure,
  unit,
  isCaution = false,
}: {
  position: TirePositionType;
  pressure: number;
  unit: string;
  isCaution?: boolean;
}) {
  return (
    <div className={`tire_info ${position}${isCaution ? ' type_caution' : ''}`}>
      <div className="tire_value">
        <span className="tire_pressure_number">{pressure}</span>
        <span className="tire_pressure_unit">{unit}</span>
      </div>
    </div>
  );
}

function TireVisualSectionView({
  frontLeft,
  frontRight,
  rearLeft,
  rearRight,
  unit,
  updateDate,
}: {
  frontLeft: TireData;
  frontRight: TireData;
  rearLeft: TireData;
  rearRight: TireData;
  unit: string;
  updateDate: string;
}) {
  const {t} = useInferableTranslation();
  return (
    <div className="section_visual visual_tire_manage">
      <ImageSelector
        className="section_visual_img"
        alt="section_visual_img"
        kiaLightSrc={() => import('@di-shared/assets/img/car_management/tire_pressure_light.png')}
        kiaDarkSrc={() => import('@di-shared/assets/img/car_management/tire_pressure.png')}
        hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure_light.png')}
        hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure.png')}
        genesisLightSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure_light.png')}
        genesisDarkSrc={() => import('@di-shared/assets/img/car_management/genesis/tire_pressure.png')}
        fallback={<div style={{height: 292}}>&nbsp;</div>}
      />
      <div className="tire_info_box">
        <TireInfoDisplay
          position="front left"
          pressure={frontLeft.pressure}
          unit={unit}
          isCaution={frontLeft.isCaution}
        />
        <TireInfoDisplay
          position="front right"
          pressure={frontRight.pressure}
          unit={unit}
          isCaution={frontRight.isCaution}
        />
        <TireInfoDisplay
          position="rear left"
          pressure={rearLeft.pressure}
          unit={unit}
          isCaution={rearLeft.isCaution}
        />
        <TireInfoDisplay
          position="rear right"
          pressure={rearRight.pressure}
          unit={unit}
          isCaution={rearRight.isCaution}
        />
      </div>
      {updateDate && <span className="tire_info_date">{t('ctn_tire_main_update_time_eu', {date: updateDate, time: ''})}</span>}
    </div>
  );
}

function TireVisualSection({vin}: {vin: string}) {
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {
    data: {data: tireBoundData},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.tire.getTireBound), vin],
    queryFn: () => diApiNa.tire.getTireBound({vin}),
  });

  const pressureUnit = getPressureUnitString(tireBoundData.firstUnit as TirePressureUnit);
  const updateDate = parseDateWithFormat(tireBoundData.tireTime);
  const updateDateString = updateDate ? CldrDatetimeFormatter.format(updateDate as Date, 'yMMMd') : '';

  /**
   * [2026-02-03:Jeyoung Lee]
   * @link https://hmg.dooray.com/project/tasks/4184331333060894239#comment-4259730504000784942
   */

  return (
    <TireVisualSectionView
      frontLeft={{pressure: tireBoundData.firstFl, isCaution: tireBoundData.flBound !== 'inbound'}}
      frontRight={{pressure: tireBoundData.firstFr, isCaution: tireBoundData.frBound !== 'inbound'}}
      rearLeft={{pressure: tireBoundData.firstRl, isCaution: tireBoundData.rlBound !== 'inbound'}}
      rearRight={{pressure: tireBoundData.firstRr, isCaution: tireBoundData.rrBound !== 'inbound'}}
      unit={pressureUnit}
      updateDate={updateDateString}
    />
  );
}

function TireVisualSectionNA({vin}: {vin: string}) {
  const defaultUnit = getPressureUnitString(0);
  const defaultPressure = '-';

  return (
    <ErrorBoundary
      fallback={
        <div className="section_visual visual_tire_manage">
          <ImageSelector
            className="section_visual_img"
            alt="section_visual_img"
            kiaLightSrc={() => import('@di-shared/assets/img/car_management/tire_pressure_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/car_management/tire_pressure.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/car_management/genesis/tire_pressure.png')}
            fallback={<div style={{height: 292}}>&nbsp;</div>}
          />
          <div className="tire_info_box">
            {(['front left', 'front right', 'rear left', 'rear right'] as TirePositionType[]).map((pos) => (
              <div
                key={pos}
                className={`tire_info ${pos}`}
              >
                <div className="tire_value">
                  <span className="tire_pressure_number">{defaultPressure}</span>
                  <span className="tire_pressure_unit">{defaultUnit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      }
    >
      <Suspense
        fallback={
          <div className="section_visual visual_tire_manage">
            <div
              className="skeleton"
              style={{height: 292, width: '100%'}}
            />
          </div>
        }
      >
        <TireVisualSection vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

// ============================================================================
// RecommendedPressureSection
// ============================================================================

/**
 * Mock 데이터: 권장 타이어 정보
 * @link https://www.figma.com/design/PkieyofRoeBBl5u5brYUlJ/branch/aefE6KSfldSYW8z38MQfLP/04.-%EC%B0%A8%EB%9F%89%EA%B4%80%EB%A6%AC?node-id=23446-93044&t=dtX6Zg3tFC6UJUH5-11
 *
 * 케이스별 테스트 시나리오:
 *
 * 1. 등록된 타이어 있음 + multi spec (수정 버튼 표출)
 *    registeredFrontTire: '245/45R20', registeredRearTire: '245/45R20'
 *
 * 2. 등록된 타이어 없음 + single spec (버튼 미표출, 첫 번째 타이어 스펙 표출)
 *    registeredFrontTire: undefined, registeredRearTire: undefined
 *    + tirePsiList의 모든 frontPsi를 35, rearPsi를 35로 수정
 *
 * 3. 등록된 타이어 없음 + multi spec (Select Tire 버튼 표출)
 *    registeredFrontTire: undefined, registeredRearTire: undefined
 *    + tirePsiList의 타이어가 서로 다른 공기압
 *
 * 4. 등록된 타이어 있음 + single spec (버튼 미표출, 등록된 타이어 스펙 표출)
 *    registeredFrontTire: '245/45R20', registeredRearTire: '245/45R20'
 *    + tirePsiList의 모든 타이어가 동일한 공기압
 */

function RecommendedPressureSectionNA({vin, puid}: {vin: string; puid: string}) {
  const {t} = useInferableTranslation();
  const defaultUnit = getPressureUnitString(0);
  const defaultPressure = '-';

  return (
    <ErrorBoundary
      fallback={
        <div className="section_container recommend_pressure">
          <div className="section_container_header">
            <h3 className="title_xsmall_b">{t('ctn_tire_Recommended_Pressure_selected_eu')}</h3>
          </div>
          <ul className="tire_pressure_list">
            <li className="tire_pressure_item">
              <span className="tire_pressure_title">{t('ctn_tire_Recommended_Pressure_selected_front_eu')}</span>
              <span className="tire_pressure_value">
                <span className="tire_pressure_number">{defaultPressure}</span>
                <span className="tire_pressure_unit">{defaultUnit}</span>
              </span>
            </li>
            <li className="tire_pressure_item">
              <span className="tire_pressure_title">{t('ctn_tire_Recommended_Pressure_selected_rear_eu')}</span>
              <span className="tire_pressure_value">
                <span className="tire_pressure_number">{defaultPressure}</span>
                <span className="tire_pressure_unit">{defaultUnit}</span>
              </span>
            </li>
          </ul>
        </div>
      }
    >
      <Suspense
        fallback={
          <div className="section_container recommend_pressure">
            <div className="section_container_header">
              <div
                className="skeleton"
                style={{width: 200, height: 20}}
              />
            </div>
            <ul className="tire_pressure_list">
              <li className="tire_pressure_item">
                <span
                  className="skeleton"
                  style={{width: 80, height: 24}}
                />
              </li>
              <li className="tire_pressure_item">
                <span
                  className="skeleton"
                  style={{width: 80, height: 24}}
                />
              </li>
            </ul>
          </div>
        }
      >
        <RecommendedPressureSectionInner
          vin={vin}
          puid={puid}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function RecommendedPressureSectionInner({vin, puid}: {vin: string; puid: string}) {
  const {t} = useInferableTranslation();
  const {resolveError} = useApiError();
  const queryClient = useQueryClient();

  const [
    {
      data: {data: tireRecommendedData},
    },
    {data: tireBoundData},
  ] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApiNa.tire.getTireRecommended1), vin],
        queryFn: () => diApiNa.tire.getTireRecommended1({vin}),
      },
      {
        queryKey: [getQueryKey(diApiNa.tire.getTireBound), vin],
        queryFn: () => diApiNa.tire.getTireBound({vin}),
      },
    ],
  });

  const [submitTireRegistration] = usePendingHandler(
    async (selectedTire: SelectedTireNA) => {
      await diApiNa.tire.createTire({requestBody: {vin, puid, frontTire: selectedTire.frontTire, rearTire: selectedTire.rearTire}});
    },
    async (error) => {
      const [execute] = await resolveError(error);
      execute();
    }
  );

  const unit: TirePressureUnit = tireBoundData.data.firstUnit;

  // hasRegisteredTire: 사용자가 이미 타이어를 등록했는지 여부 (앞/뒤 중 하나라도 등록되어 있으면 true)
  const hasRegisteredTire = !!(tireRecommendedData.registeredFrontTire || tireRecommendedData.registeredRearTire);

  // isSingleSpecTire: 모든 타이어 스펙의 권장 공기압이 동일한지 여부 (앞/뒤 모두 같은 권장값이면 true)
  const isSingleSpecTire = isSingleSpec(tireRecommendedData.tirePsiList);

  // 권장 타이어 데이터 변환
  const recommendedData = convertToRecommendedTireData(tireRecommendedData, unit, t);

  /**
   * 버튼 표출 조건
   * showSelectButton: "Select Tire" 버튼 표시 여부 (타이어 미등록 + 여러 스펙이 있는 경우) (미등록이어도 스펙이 모두 동일하면 노출 x)
   * showEditButton: 타이어 수정 버튼 표시 여부 (타이어 등록됨 + 여러 스펙이 있는 경우) (스펙이 모두 동일하면 노출 x)
   * hasPressureData: 권장 타이어 압력 데이터가 존재하는지 여부
   * @link https://www.figma.com/design/PkieyofRoeBBl5u5brYUlJ/branch/aefE6KSfldSYW8z38MQfLP/04.-%EC%B0%A8%EB%9F%89%EA%B4%80%EB%A6%AC?node-id=23446-93044&t=a8HPINfdwesNhS9k-11
   */
  const showSelectButton = !hasRegisteredTire && !isSingleSpecTire;
  const showEditButton = hasRegisteredTire && !isSingleSpecTire;
  const pressureUnit = getPressureUnitString(unit);

  /**
   * 타이어 선택/수정 바텀시트를 열고 선택된 타이어로 등록 API 호출
   * Edit와 Select 모두 같은 바텀시트를 사용
   */
  async function openTireSelectionBottomSheet() {
    const selectedTire = await ccWebviewUtils.openBottomSheetWebViewUrl<SelectedTireNA>(
      ROUTE_PATHS.BOTTOM_SHEET_DIAG_TIRE_SELECTION.getFullPath(vin),
      {vin, tireRecommendedData}
    );
    if (selectedTire) {
      WebviewUtils.loading(true);
      await submitTireRegistration(selectedTire);
      await queryClient.invalidateQueries({queryKey: [getQueryKey(diApiNa.tire.getTireRecommended1), vin]});
      WebviewUtils.loading(false);
    }
  }

  const frontPressure: PressureItem = recommendedData?.frontPressure ?? {pressure: 0, unit: pressureUnit};
  const rearPressure: PressureItem = recommendedData?.rearPressure ?? {pressure: 0, unit: pressureUnit};

  return (
    <div className="section_container recommend_pressure">
      <div className="section_container_header">
        <h3 className="title_xsmall_b">{t('ctn_tire_Recommended_Pressure_selected_eu')}</h3>
        {showEditButton && recommendedData?.tireName && (
          <button
            type="button"
            className="tire_edit_btn"
            onClick={openTireSelectionBottomSheet}
          >
            <span className="ic ic_edit_12px" />
            <span className="tire_name">{recommendedData.tireName}</span>
          </button>
        )}
      </div>
      <ul className="tire_pressure_list">
        <li className="tire_pressure_item">
          <span className="tire_pressure_title">{t('ctn_tire_Recommended_Pressure_selected_front_eu')}</span>
          <span className="tire_pressure_value">
            <span className="tire_pressure_number">{recommendedData ? frontPressure.pressure : '-'}</span>
            <span className="tire_pressure_unit">{frontPressure.unit}</span>
          </span>
        </li>
        <li className="tire_pressure_item">
          <span className="tire_pressure_title">{t('ctn_tire_Recommended_Pressure_selected_rear_eu')}</span>
          <span className="tire_pressure_value">
            <span className="tire_pressure_number">{recommendedData ? rearPressure.pressure : '-'}</span>
            <span className="tire_pressure_unit">{rearPressure.unit}</span>
          </span>
        </li>
      </ul>
      {showSelectButton && (
        <button
          type="button"
          className="btn btn_large btn_filled_tonal btn_full"
          onClick={openTireSelectionBottomSheet}
        >
          {t('ctn_tire_Recommended_Pressure_select_eu')}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface TirePressureNaProps {
  vin: string;
  puid: string;
}

function TirePressureNa({vin, puid}: TirePressureNaProps) {
  const {t} = useInferableTranslation();
  const {openAlert} = useCcWebviewUtilsHelper();

  const [tireBoundQuery, tireRecommendedQuery] = useQueries({
    queries: [
      {queryKey: [getQueryKey(diApiNa.tire.getTireBound), vin], queryFn: () => diApiNa.tire.getTireBound({vin})},
      {queryKey: [getQueryKey(diApiNa.tire.getTireRecommended1), vin], queryFn: () => diApiNa.tire.getTireRecommended1({vin})},
    ],
  });

  const shouldShowErrorPopup =
    tireBoundQuery.isError ||
    tireRecommendedQuery.isError ||
    (!tireBoundQuery.isLoading && !tireBoundQuery.data?.data) ||
    (!tireRecommendedQuery.isLoading && !tireRecommendedQuery.data?.data);

  useEffect(() => {
    if (!shouldShowErrorPopup) {
      return;
    }
    openAlert({
      title: t('ctn_tire_error_title'),
      message: t('msg_tire_apierror_popup_eu'),
      okText: t('ctn_tire_error_ok'),
    });
  }, [shouldShowErrorPopup]);

  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_tire_main_title_eu')}</h1>
      </Header>
      <main
        id="content"
        className="content page_car_management"
      >
        <section className="section section_eu_tire_pressure">
          <h2 className="blind">{t('ctn_tire_main_title_eu')}</h2>
          <TireVisualSectionNA vin={vin} />
          <RecommendedPressureSectionNA
            vin={vin}
            puid={puid}
          />
          <InstructionSection />
        </section>
        <div className="divider divider_thick" />
        <NoticeSection vin={vin} />
      </main>
    </div>
  );
}

export default TirePressureNa;
